import React, { useCallback, useEffect, useState } from 'react';
import Loader from '../../component/common/Loader';
import SearchInput from '../../component/common/Input/SearchInput';
import Table from '../../component/common/Table';
import Pagination from '../../component/common/table2/Pagination';
import { fetchBreathworkUsers } from './breathworkHubApi';
import { TableCard } from '../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Shared, searchable users table for the Breathwork section (columns are
 * session-shaped, so this is separate from the Sleep users table).
 * B2C Logs renders it unscoped (accountType="B2C"); the company page scopes
 * it via companyId; a future company panel passes its own `fetcher`.
 */
const BreathworkUsersTable = ({
  accountType = '',
  companyId = '',
  onOpenUser,
  tableName,
  fetcher = fetchBreathworkUsers,
  showCompanyColumn = !companyId,
  // Optional: narrows the list to one department. Defaults to '' so the
  // super-admin Breathwork Hub pages behave exactly as before.
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({
          page,
          perPage: PER_PAGE,
          search,
          accountType,
          companyId,
          department
        });
        // Users with no sessions yet (company mode) show dashes, not zeros.
        const mapped = (response.data || []).map((row) => ({
          ...row,
          holdText: row.totalSessions > 0 ? `${row.holdSessions ?? 0}` : '-',
          exhaleText: row.totalSessions > 0 ? `${row.exhaleSessions ?? 0}` : '-',
          streakText: row.currentStreak > 0 ? `${row.currentStreak} days` : '-',
          lastSessionDate: row.lastSessionDate || '-'
        }));
        setRows(mapped);
        setTotalCount(response.totalCount || 0);
      } catch (error) {
        console.error('BreathworkUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoader(false);
      }
    },
    [accountType, companyId, fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Sessions', key: 'totalSessions', sortable: false, align: 'center', type: 'number' },
    { title: 'Hold', key: 'holdText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Exhale', key: 'exhaleText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Streak', key: 'streakText', sortable: false, align: 'center', type: 'text3' },
    { title: 'Last Session', key: 'lastSessionDate', sortable: false, align: 'center', type: 'text3' },
    { title: '', key: 'action', align: 'right', type: 'action', arrowClickFun: onOpenUser }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      
    </div>
  );
};

export default BreathworkUsersTable;
